import { ChangeEvent, useState } from "react";
import { isEmpty } from "lodash";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { EmailContactForm } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/AddContactForm/EmailContactForm";
import { PhoneContactForm } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/AddContactForm/PhoneContactForm";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";

interface IInvitationContactEmail {
    contactPointId: string;
    address: string;
}

interface IInvitationContactPhone {
    contactPointId: string;
    phoneTypeId: string;
    countryLookupId: string;
    number: string;
}

interface IAddContactFormProps {
    email?: IInvitationContactEmail;
    onEmailChange?: (email?: IInvitationContactEmail) => void;
    phone?: IInvitationContactPhone;
    onPhoneChange?: (phone?: IInvitationContactPhone) => void;
}

function AddContactForm(props: IAddContactFormProps): JSX.Element {
    const { getText } = useTranslation();
    const [contactType, setContactType] = useState<string>(props.email?.contactPointId ?? "");

    const isAddingEmail = contactType?.split("_")?.length === 1;

    const isAddingPhone = contactType?.split("_")?.length === 2;

    const renderEmailOption = (type: FlatLookupView): JSX.Element => (
        <option key={type.LookupID} value={type.LookupID}>
            {cacheStore.GetLocalizedValueByLookupId(type.LookupID)} - {getText("EmailLabel")}
        </option>
    );

    const handleChange = (e: ChangeEvent<HTMLSelectElement>): void => {
        const { value } = e.target;

        setContactType(value);

        const contact = value.split("_") as [string] | [string, string];

        // checks if selected contact is email or phone (phone contact option value has a "_" separator)
        if (contact.length === 1) {
            const [contactPointId] = contact;

            props.onEmailChange({ ...props.email, contactPointId, address: "" });
            props.onPhoneChange?.();
        }
        else {
            const [contactPointId, phoneTypeId] = contact;

            props.onPhoneChange({
                ...props.phone,
                phoneTypeId,
                contactPointId,
                countryLookupId: cacheStore.GetDefaultCountryForActiveSite().LookupID,
                number: ""
            });
            props.onEmailChange?.();
        }
    };

    const renderPhoneOption = (type: FlatLookupView, phone: FlatLookupView): JSX.Element => (
        <option
            key={`${type.LookupID}_${phone.LookupID}`}
            value={`${type.LookupID}_${phone.LookupID}`}
        >
            {`${cacheStore.GetLocalizedValueByLookupId(type.LookupID)} - ${cacheStore.GetLocalizedValueByLookupId(phone.LookupID)}`}
        </option>
    );

    const phoneContactPointTypes = (): JSX.Element[] => {
        let contactPointType = cacheStore.FindLookupsByTypeCode("CONTACTPOINTTYPE");
        let phoneLookup = cacheStore.FindLookupByLookupCode("MOBILE");

        return contactPointType.map(cp => renderPhoneOption(cp, phoneLookup));
    };

    return (
        <div className="margin-lg-left">
            <div className={!contactType ? "form-group margin-md-bottom has-error" : "form-group margin-md-bottom"}>
                <label htmlFor="contactPointType">
                    <strong>{getText("TypeLabel")}</strong>
                </label>

                <select
                    required
                    autoFocus
                    name="type"
                    id="contactPointType"
                    className="form-control width-60-percent"
                    value={contactType}
                    onChange={handleChange}
                >
                    {cacheStore.GetEmailContactPointTypeLookups().map(renderEmailOption)}
                    {cacheStore.ClinicSettings_Portal.EnablePortalV2 && phoneContactPointTypes()}
                </select>

                {isEmpty(contactType) && (
                    <span className="red-text">{getText("PortalInvitation_InvalidContactLabel")}</span>
                )}
            </div>

            {isAddingEmail && (
                <EmailContactForm
                    value={props.email.address}
                    onChange={(value: string) => props.onEmailChange?.({ ...props.email, address: value })}
                />
            )}

            {isAddingPhone && (
                <PhoneContactForm
                    phoneNumber={props.phone.number}
                    countryLookupId={props.phone.countryLookupId}
                    onPhoneNumberChange={number => props.onPhoneChange?.({ ...props.phone, number })}
                    onCountryLookupChange={countryLookupId => props.onPhoneChange?.({ ...props.phone, countryLookupId })}
                />
            )}
        </div>
    );
}

export type { IInvitationContactEmail, IInvitationContactPhone };

export { AddContactForm };
